/**
 * Placement defaults (D36): the parameter values a newly placed component or
 * newly drawn line starts with, so a schematic is captured at the right tube
 * or channel size rather than fixed element by element afterwards.
 *
 * Two layers over the template's own defaults, keyed by template entity so a
 * newer template version keeps them:
 *
 * - the design's, in the document (`placement_defaults`), so they version with
 *   it, travel with a fork, and are the same for everyone who edits it;
 * - this person's, kept in the browser, for designs that set none.
 *
 * The design's win, key by key. Nothing reads defaults after creation: the
 * values are stamped onto the element, so every element carries its own and
 * physics never consults a table (invariant 4). All values are canonical SI.
 */
import type { DesignDocument, ParameterDeclaration, TemplateDocument } from "../design/document";

/** Template entity id -> parameter key -> canonical value. */
export type PlacementDefaults = Readonly<Record<string, Readonly<Record<string, unknown>>>>;

export const NO_DEFAULTS: PlacementDefaults = {};

// Matches BOUND_TOLERANCE in workspace/parameters.ts.
const BOUND_TOLERANCE = 1e-12;

/**
 * Whether a stored default still suits the declaration. A template's newer
 * version may narrow a bound or drop an enum value; a default it no longer
 * accepts is passed over, and the template's own default applies.
 */
export function suitsDeclaration(declaration: ParameterDeclaration, value: unknown): boolean {
  switch (declaration.value_type) {
    case "float":
    case "int": {
      if (typeof value !== "number" || !Number.isFinite(value)) return false;
      if (declaration.value_type === "int" && !Number.isInteger(value)) return false;
      const slack = (bound: number) => BOUND_TOLERANCE * Math.max(Math.abs(value), Math.abs(bound));
      const { minimum, maximum } = declaration;
      if (minimum != null && value < minimum - slack(minimum)) return false;
      if (maximum != null && value > maximum + slack(maximum)) return false;
      return true;
    }
    case "bool":
      return typeof value === "boolean";
    case "enum":
      return typeof value === "string" && (declaration.enum_values ?? []).includes(value);
    case "str":
      return typeof value === "string";
    default:
      return false;
  }
}

/**
 * The values a new element of this template starts with: the template's
 * defaults, then this person's, then the design's. Only declared parameters,
 * and only values the declaration accepts.
 */
export function startingValues(
  template: TemplateDocument,
  entityId: string | undefined,
  design: DesignDocument,
  user: PlacementDefaults,
): Record<string, unknown> {
  const layers = entityId ? [user[entityId], design.placement_defaults?.[entityId]] : [];
  const values: Record<string, unknown> = {};
  for (const declaration of template.parameters ?? []) {
    let value: unknown = declaration.default ?? undefined;
    for (const layer of layers) {
      const candidate = layer?.[declaration.key];
      if (candidate !== undefined && suitsDeclaration(declaration, candidate)) value = candidate;
    }
    if (value !== undefined && value !== null) values[declaration.key] = value;
  }
  return values;
}

/** Set, or with null clear, one template's defaults, dropping it when it sets nothing. */
export function withTemplateDefaults(
  defaults: PlacementDefaults,
  entityId: string,
  values: Readonly<Record<string, unknown>> | null,
): PlacementDefaults {
  const { [entityId]: _previous, ...rest } = defaults;
  void _previous;
  return values && Object.keys(values).length > 0 ? { ...rest, [entityId]: { ...values } } : rest;
}
